// Pure derivation for the customer tracking page's progress tracker.
//
// Every step's completion comes from the order state machine (onward-hasura
// `docs/EVENT_TRANSITIONS.md`) or from an order column, never from the before/after pairs
// `useCrossdockingSteps` builds. Those pairs describe the events *bracketing* a crossdock step,
// which is one state away from the event that owns the step's timestamp — the source of the
// off-by-one completions and `Invalid Date` stamps this replaces.
//
// No React, no Apollo. See __test__/trackerSteps.test.js.

import {
    ATTEMPT_RESOLVING_STATES,
    DELIVERY_ATTEMPT_ACTIONS,
    LTL_STEP_STATES,
    ORDER_COMPLETE_STATUSES,
    REWIND_CLEARS,
    STATE_DATE_FIELDS,
    STEP_KEYS,
    STEP_LABELS,
} from './constants';
import { formatDateInTimezone, getOrderTimezone } from '@/utilities/addFormattedDateFields';
import { getStopCompletionTimes } from './utilities';

const pickDate = (event, state) => {
    const fields = STATE_DATE_FIELDS[state] || [];
    for (const field of fields) {
        if (event?.[field]) return event[field];
    }
    return null;
};

/**
 * The state the order is in, derived the way Hasura's `orders_derive_event_state` derives it:
 * the `transition.next` of the most recent event, defaulting to START.
 *
 * `wh_events` arrives from the order query sorted ascending by
 * `created_at, transition.current, event_id` — the exact reverse of the DB's ordering — so the
 * last element is the most recent event. Exported so tests can pin the replay below against the
 * same state the column would hold.
 *
 * @param {object[]} whEvents order_wh_events in query (ascending) order
 * @returns {string} an `orders.event_state` value
 */
export const deriveEventState = (whEvents) => {
    const events = whEvents || [];
    return events[events.length - 1]?.transition?.next || 'START';
};

/**
 * Replay the event log into the set of states the order has entered and not fallen back out of.
 *
 * A state is entered by the event whose `transition.next` names it, and that event owns the
 * state's timestamp. Rewind actions delete the states they invalidate (see REWIND_CLEARS), so a
 * failed attempt un-completes the step it undid and nothing else. Re-entering a state keeps the
 * earlier timestamp when the new event carries none — `END:REVERT` re-enters CD_COMPLETE with no
 * `pickup_date` and must not blank out the original one.
 *
 * @param {object[]} whEvents order_wh_events in query (ascending) order
 * @returns {Map<string, {event: object, date: string|null}>} keyed by state
 */
export const reachedStates = (whEvents) => {
    const reached = new Map();

    (whEvents || []).forEach((event) => {
        (REWIND_CLEARS[event?.action] || []).forEach((state) => reached.delete(state));

        const state = event?.transition?.next;
        if (!state) return;

        const prior = reached.get(state);
        reached.set(state, {
            event,
            date: pickDate(event, state) || prior?.date || event?.created_at || null,
        });
    });

    return reached;
};

/**
 * Whether the order has a middle mile leg worth showing the customer. Mirrors
 * `hasMiddleMileLeg` in @onward-delivery/core: `middle_mile_is_direct` means the shipper hauls
 * the freight to the warehouse itself and no tracking reaches us, so those orders get no LTL
 * steps rather than three that can never progress.
 *
 * Deliberately not `priced_as_middle_mile` — that is a pricing flag, not a tracking signal.
 */
export const hasMiddleMileLeg = (order) => !!order?.has_middle_mile_origin && !order?.middle_mile_is_direct;

/** Whether a crossdock leg was ever added. Same test the internal LTL checklist uses. */
export const isCrossdocked = (order) => (order?.wh_events || []).some((e) => e?.action?.endsWith(':ADD_CD'));

/**
 * The most recent failed delivery, if the order has not moved past it. Returns the attempt event
 * so the step can be stamped, or null. Attempt events are written by the exception flow as a
 * bare `{ action, order_id }`, so `created_at` is the only timestamp they carry.
 */
export const openDeliveryAttempt = (order) => {
    const events = order?.wh_events || [];

    const attemptIdx = events.findLastIndex((e) => DELIVERY_ATTEMPT_ACTIONS.includes(e?.action));
    if (attemptIdx < 0) return null;

    const movedOnIdx = events.findLastIndex((e) => ATTEMPT_RESOLVING_STATES.includes(e?.transition?.next));
    return movedOnIdx > attemptIdx ? null : events[attemptIdx];
};

// `date` is an instant, rendered with a time. `dateText` is an already-formatted calendar date
// for a column that holds one — see the SCHEDULED step — and renders without a time.
//
// An open step never carries either: a date beside an unchecked 'Timeframe Confirmed' reads as the
// time it was confirmed, when it is only the window we proposed.
const step = (key, { completed = false, date = null, dateText = null, ...rest } = {}) => ({
    key,
    label: STEP_LABELS[key],
    completed: !!completed,
    date: completed ? date || null : null,
    dateText: completed ? dateText || null : null,
    ...rest,
});

// First state in `states` that the order has entered, for a step that several states satisfy.
const firstReached = (reached, states) => {
    for (const state of states) {
        const entry = reached.get(state);
        if (entry) return entry;
    }
    return null;
};

const ltlSteps = (reached) =>
    Object.entries(LTL_STEP_STATES).map(([key, states]) => {
        const entry = firstReached(reached, states);
        return step(key, { completed: !!entry, date: entry?.date });
    });

const crossdockSteps = (reached) =>
    [
        [STEP_KEYS.CD_IN_TRANSIT, 'CD_PENDING'],
        [STEP_KEYS.CD_ARRIVED, 'CD_RECEIVED'],
    ].map(([key, state]) => {
        const entry = reached.get(state);
        return step(key, { completed: !!entry, date: entry?.date });
    });

/**
 * The highlight, as a single frontier: the first open step past the last completed one.
 *
 * Comparing each step with its immediate predecessor highlights two steps at once now that the
 * machine can legitimately skip one for good — a crossdock order that took the CD_PENDING:CONFIRM
 * shortcut never enters CD_RECEIVED, so 'Arrived at Local Facility' stays open behind a completed
 * 'In Transit', and it would still read as in-progress after the order was delivered.
 */
const withActive = (steps) => {
    const lastCompleted = steps.reduce((acc, step, idx) => (step.completed ? idx : acc), -1);
    const frontier = steps.findIndex((step, idx) => idx > lastCompleted && !step.completed);

    return steps.map((step, idx) => ({ ...step, active: idx === frontier }));
};

/**
 * Build the ordered step list for the tracker.
 *
 * Middle mile steps appear only for orders with a trackable middle mile leg, and crossdock steps
 * only for orders that were actually crossdocked, so a plain E2E delivery shows neither.
 *
 * @param {object} args
 * @param {object} args.order the order, with `wh_events` and their `transition`
 * @param {object} [args.route] the customer-facing route mapping's route, when the order is on one
 * @param {object} [args.firstReview] the customer's rating, when they have left one
 * @returns {object[]} steps in display order: `{ key, label, completed, active, date, dateText }`
 */
export const buildTrackerSteps = ({ order, route, firstReview }) => {
    if (!order) return [];

    const reached = reachedStates(order.wh_events);
    const { stopPickupTime, stopDeliveryTime } = route
        ? getStopCompletionTimes(order.order_id, route)
        : { stopPickupTime: null, stopDeliveryTime: null };

    const teamAssigned = !!order.carrier_id || !!route;
    const outForDelivery = reached.get('CD_COMPLETE');
    const delivered = reached.get('END');
    const attempt = openDeliveryAttempt(order);

    // order_status stays authoritative for completion: it is what the rest of the page reads,
    // and it is set on paths that do not always reach END.
    const isComplete = ORDER_COMPLETE_STATUSES.includes(order.order_status) || !!stopDeliveryTime || !!delivered;

    return withActive([
        step(STEP_KEYS.PLACED, { completed: true, date: order.created_at }),
        step(STEP_KEYS.TEAM_ASSIGNED, {
            completed: teamAssigned,
            date: route?.created_at || order.claimed_date,
        }),

        ...(hasMiddleMileLeg(order) ? ltlSteps(reached) : []),

        // delivery_date is a calendar date stored as UTC midnight, so `new Date(...)` renders the
        // day before in every US timezone — and a 12:00 AM time on it is meaningless. Formatted
        // through the same tz-aware helper backing `delivery_date_formatted`.
        step(STEP_KEYS.SCHEDULED, {
            completed: !!order.delivery_date,
            dateText: formatDateInTimezone(order.delivery_date, getOrderTimezone(order), 'EEE, MMM d, yyyy'),
        }),
        // Only 'confirmed' counts. delivery_time_confirmed also holds 'pending', 'rejected',
        // 'no response', 'SMS failed', 'voicemail' and 'failed' — every route-activation gate in
        // the app tests it for equality, and so does this step's own Confirm CTA.
        step(STEP_KEYS.TIMEFRAME_CONFIRMED, {
            completed: !!order.del_window_start && order.delivery_time_confirmed === 'confirmed',
            date: order.del_window_start,
        }),

        ...(isCrossdocked(order) ? crossdockSteps(reached) : []),

        step(STEP_KEYS.OUT_FOR_DELIVERY, {
            completed: !!stopPickupTime || !!outForDelivery,
            date: stopPickupTime?.toISOString() || outForDelivery?.date,
        }),
        ...(attempt ? [step(STEP_KEYS.DELIVERY_ATTEMPTED, { completed: true, date: attempt.created_at })] : []),
        step(STEP_KEYS.DELIVERY_COMPLETE, {
            completed: isComplete,
            date: stopDeliveryTime?.toISOString() || delivered?.date || order.completion_time,
        }),
        step(STEP_KEYS.REVIEW_SUBMITTED, { completed: !!firstReview, date: firstReview?.created_at }),
    ]);
};
